import { useSelector } from "react-redux";
import { Fragment, useState, useEffect, useCallback } from "react";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { RootState } from "app/store";
import { DialogType, RestrictedFeatureSource } from "app/types";
import I18n from "common/i18n";
import { CloseBtn, FadeTransition } from "common/elements";
import { btnDelay, usePlaySound, useScale } from "common/utils";
import { ParentGate } from "features/parent/ParentGate";
import { useAccountLimits, usePlanLimits } from "features/subscription/hooks";
import { recordRegisterPromptShow, recordRegisterPromptClose } from "./registerAnalytics";

const getMessageKeys = (
  source: RestrictedFeatureSource,
): { titleKey: string; messageKey: string } => {
  switch (source) {
    case RestrictedFeatureSource.COURSE:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_COURSE",
        messageKey: "MSG_PARENT_REGISTRATION_MESSAGE_COURSE",
      };
    case RestrictedFeatureSource.PROJECT_LIMIT:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_PROJECT_LIMIT",
        messageKey: "MSG_PARENT_REGISTRATION_MESSAGE_PROJECT_LIMIT",
      };
    case RestrictedFeatureSource.CHILD_USER_LIMIT:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_CHILD_USER_LIMIT",
        messageKey: "MSG_PARENT_REGISTRATION_MESSAGE_CHILD_USER_LIMIT",
      };
    case RestrictedFeatureSource.AI_CHAT_LIMIT:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_AI_CHAT_LIMIT",
        messageKey: "MSG_PARENT_REGISTRATION_MESSAGE_AI_CHAT_LIMIT",
      };
    case RestrictedFeatureSource.AI_IMAGE_LIMIT:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_AI_IMAGE_LIMIT",
        messageKey: "MSG_PARENT_REGISTRATION_MESSAGE_AI_IMAGE_LIMIT",
      };
    case RestrictedFeatureSource.ASSET_LIMIT:
      return {
        titleKey: "MSG_RESTRICTED_FEATURE_TITLE_ASSET_LIMIT",
        messageKey: "MSG_PARENT_REGISTRATION_MESSAGE_ASSET_LIMIT",
      };
  }
};

export const ParentRegistrationPromptDialog = () => {
  const play = usePlaySound();
  const limits = usePlanLimits();
  const accountLimits = useAccountLimits();
  const handleDialog = useDialog();
  const { scale } = useScale();
  const redirect = useSelector(
    (state: RootState) => state.config.dialog.parent_registration_prompt,
  );
  const [titleKey, setTitleKey] = useState("");
  const [messageKey, setMessageKey] = useState("");
  const [source, setSource] = useState<RestrictedFeatureSource | undefined>(undefined);
  const [gateVisible, setGateVisible] = useState(false);
  const visible = redirect.value && !!source;

  useEffect(() => {
    if (redirect.value) {
      const src = redirect.args?.source as RestrictedFeatureSource | undefined;
      if (!src) return;
      setSource(src);
      recordRegisterPromptShow(src);
      const { titleKey: title, messageKey: message } = getMessageKeys(src);
      setTitleKey(title);
      setMessageKey(message);
    }
  }, [redirect]);

  const getLimit = (): number => {
    switch (source) {
      case RestrictedFeatureSource.PROJECT_LIMIT:
        return limits.projects.limit;
      case RestrictedFeatureSource.CHILD_USER_LIMIT:
        return accountLimits.players.limit;
      case RestrictedFeatureSource.AI_CHAT_LIMIT:
        return limits.aiChatDaily.limit;
      case RestrictedFeatureSource.AI_IMAGE_LIMIT:
        return limits.aiImageDaily.limit;
      case RestrictedFeatureSource.ASSET_LIMIT:
        return limits.assets.limit;
      default:
        return 0;
    }
  };

  const close = useCallback(() => {
    handleDialog({ type: DialogType.PARENT_REGISTRATION_PROMPT, value: false });
  }, [handleDialog]);

  const handleClose = () => {
    play();
    if (source) {
      recordRegisterPromptClose(source);
    }
    btnDelay(() => {
      close();
    });
  };

  const handleOpenGate = () => {
    btnDelay(() => {
      play();
      setGateVisible(true);
    });
  };

  const handleGateClose = () => {
    setGateVisible(false);
  };

  const handleGateConfirm = () => {
    setGateVisible(false);
    close();
    handleDialog({
      type: DialogType.PARENT_REGISTRATION,
      value: true,
      args: { redirectAfterSuccess: "/parent", source: source ?? "unknown" },
    });
  };

  return (
    <>
      <Dialog
        open={visible}
        maxWidth={false}
        sx={{ zIndex: 2147483646 }}
        TransitionComponent={FadeTransition}
        componentsProps={{
          backdrop: { style: { backgroundColor: "transparent" } },
        }}
        PaperProps={{
          style: {
            margin: 0,
            overflow: "visible",
            backgroundColor: "transparent",
          },
          elevation: 0,
        }}
      >
        <div className="flex-col-el flex-center relative" style={{ transform: `scale(${scale})` }}>
          <div
            className="flex-col-el flex-center relative"
            style={{
              filter:
                "drop-shadow(0px 6px 6px rgb(0 0 0 / 20%)) drop-shadow(0px 10px 14px rgb(0 0 0 / 14%)) drop-shadow(0px 4px 18px rgb(0 0 0 / 12%))",
            }}
          >
            <div className="flex-col-el justify-start w-[650px] relative border-solid border-white border-[8px] rounded-lg bg-white">
              <CloseBtn close={handleClose} />

              <div className="flex-col-view justify-start w-full h-full relative p-6 rounded bg-beige-60">
                <div className="flex-row-view items-center justify-start pb-4">
                  <div className="flex-col-center mr-2">
                    <span className="material-symbols-outlined type-title text-[#4C9DF1]">
                      info
                    </span>
                  </div>
                  <p className="text-body text-textcolor-black type-title">{I18n.t(titleKey)}</p>
                </div>

                <div className="flex-col-view items-start py-4">
                  {I18n.t(messageKey, { limit: getLimit() })
                    .split("\n")
                    .map((line, index) => (
                      <div key={index} className="flex-row-view flex-wrap mb-3">
                        <p className="text-body text-textcolor-black type-body">・</p>
                        <p className="text-body text-textcolor-black type-body flex-1">
                          {line.split("<br>").map((part, i, arr) => (
                            <Fragment key={i}>
                              {part}
                              {i < arr.length - 1 && <br />}
                            </Fragment>
                          ))}
                        </p>
                      </div>
                    ))}
                  <div className="flex-row-view flex-wrap mb-3">
                    <p className="text-body text-textcolor-black type-body">・</p>
                    <p className="text-body text-textcolor-black type-body flex-1">
                      {I18n.t("MSG_PARENT_REGISTRATION_PROMPT_CTA_HINT")
                        .split("<br>")
                        .map((part, i, arr) => (
                          <Fragment key={i}>
                            {part}
                            {i < arr.length - 1 && <br />}
                          </Fragment>
                        ))}
                    </p>
                  </div>
                </div>

                <div className="flex-row-el flex-center gap-6 mt-4 px-0 alpha">
                  <button className="btn btn-blue" onClick={handleClose}>
                    <div className="flex-row-center gap-1 mb-1">
                      <span className="material-symbols-outlined text-font-size-h3 text-white">
                        close
                      </span>
                      <p className="text-body text-white text-font-size-body1">
                        {I18n.t("MSG_BTN_LATER")}
                      </p>
                    </div>
                  </button>

                  <button className="btn btn-primary" onClick={handleOpenGate}>
                    <div className="flex-row-center gap-1 mb-1">
                      <span className="material-symbols-outlined text-font-size-h3 text-white">
                        notification_important
                      </span>
                      <p className="text-body text-white text-font-size-body1">
                        {I18n.t("MSG_PARENT_REGISTRATION_PROMPT_CTA")}
                      </p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      <ParentGate visible={gateVisible} confirm={handleGateConfirm} close={handleGateClose} />
    </>
  );
};
